/**
 * useDialogA11y — the focus contract an `aria-modal="true"` dialog promises (CB-5.11 a11y remediation).
 *
 * When `open` flips true: remembers the previously-focused element, moves focus into the dialog, and
 * while open (a) closes on Escape and (b) traps Tab/Shift+Tab within the dialog's focusable elements.
 * When it closes/unmounts: restores focus to the element that opened it. Returns a ref to put on the
 * dialog container. Reused by DeclineToSign + WithdrawConsent so the trap isn't duplicated or skipped.
 */

import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useDialogA11y<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    const previouslyFocused = (typeof document !== 'undefined' ? document.activeElement : null) as
      | HTMLElement
      | null;

    const focusables = (): HTMLElement[] =>
      container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];

    // Move focus into the dialog (first focusable, else the container itself).
    const first = focusables()[0];
    (first ?? container)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !container) return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === firstEl || !container.contains(active))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Restore focus to whatever opened the dialog.
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  return containerRef;
}
